import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { KPICard } from "@/components/KPICard";
import { FlagCard } from "@/components/FlagCard";
import { StatusBadge } from "@/components/StatusBadge";
import { usd, pct, ddmmmyy, statusTone, plTone, plLabel } from "@/lib/format";
import { Check, AlertCircle, HelpCircle, Download, Trash2 } from "lucide-react";

const MatchIcon = ({ status }) => {
  if (status === "EXACT") return <Check size={16} className="text-emerald-600" />;
  if (status === "COMPUTED") return <AlertCircle size={16} className="text-blue-600" />;
  return <HelpCircle size={16} className="text-red-600" />;
};

const exportCSV = (rows, filename) => {
  if (!rows.length) return;
  const keys = Object.keys(rows[0]);
  const csv = [
    keys.join(","),
    ...rows.map((r) => keys.map((k) => {
      const v = r[k];
      if (v === null || v === undefined) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    }).join(",")),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const Result = () => {
  const { reference } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isAdmin = user?.role === "admin";

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await apiClient.delete(`/shipments/${reference}`);
      toast.success(`Deleted ${reference}`);
      navigate("/shipments");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
      setDeleting(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    apiClient.get(`/shipments/${reference}`).then(({ data }) => {
      if (mounted) { setS(data); setLoading(false); }
    }).catch((err) => {
      toast.error(err.response?.data?.detail || "Not found");
      navigate("/shipments");
    });
    return () => { mounted = false; };
  }, [reference, navigate]);

  if (loading) return <div className="text-gray-500">Loading…</div>;
  if (!s) return null;

  const plT = plTone(s.pl_amount);
  const plL = plLabel(s.pl_amount);

  const exportExcel = () => {
    const rows = (s.lines || []).map((l) => ({
      part_number: l.part_number,
      description: l.description,
      po_number: l.po_number,
      quantity: l.quantity,
      unit_price: l.unit_price,
      hts_code: l.hts_code,
      tariff_pct: l.tariff_pct,
      freight_per_unit: l.freight_per_unit,
      estimated_duty: l.estimated_duty,
      match_status: l.match_status,
    }));
    exportCSV(rows, `${s.reference}-lines.csv`);
    toast.success("Exported CSV");
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-gray-500 mb-1">Reconciliation result</div>
          <h1 className="text-2xl font-bold text-[#0F2044] font-mono" data-testid="result-reference">{s.reference}</h1>
          <div className="mt-2 grid grid-cols-2 md:grid-cols-6 gap-x-6 gap-y-1 text-sm">
            <div><span className="text-gray-500">Invoice </span><span className="font-mono">{s.invoice_number}</span></div>
            <div><span className="text-gray-500">Customer </span>{s.customer_name}</div>
            <div><span className="text-gray-500">Entry </span><span className="font-mono">{s.entry_number}</span></div>
            <div><span className="text-gray-500">Imported </span><span className="uppercase-date">{ddmmmyy(s.import_date)}</span></div>
            <div><span className="text-gray-500">Incoterm </span>{s.incoterm}</div>
            <div><span className="text-gray-500">Origin </span>{s.country_of_origin}</div>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="export-excel"
            onClick={exportExcel}
            className="flex items-center gap-2 px-3 py-2 text-sm rounded-md bg-white hover:bg-slate-50 transition-colors"
            style={{ border: "1px solid var(--color-border)" }}
          >
            <Download size={14} /> Export CSV
          </button>
          <button
            data-testid="export-pdf"
            onClick={() => window.print()}
            className="flex items-center gap-2 px-3 py-2 text-sm rounded-md bg-white hover:bg-slate-50 transition-colors"
            style={{ border: "1px solid var(--color-border)" }}
          >
            <Download size={14} /> Export PDF
          </button>
          {isAdmin && (
            <button
              data-testid="delete-shipment"
              onClick={() => setConfirmDelete(true)}
              className="flex items-center gap-2 px-3 py-2 text-sm rounded-md text-red-700 hover:bg-red-50 transition-colors"
              style={{ border: "1px solid #FCA5A5" }}
            >
              <Trash2 size={14} /> Delete
            </button>
          )}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <KPICard testId="kpi-estimated" label="Estimated Duty" value={usd(s.total_estimated_duty)} tone="info" />
        <KPICard testId="kpi-actual" label="Actual Duty and Fee Paid" value={usd(s.total_actual_duty)} tone="neutral" />
        <KPICard
          testId="kpi-variance"
          label={plL}
          value={usd(s.pl_amount)}
          sub={`CBP delta ${usd(s.variance_usd)} · ${pct(s.variance_pct)}`}
          tone={plT}
        />
        <KPICard
          testId="kpi-status"
          label="Status"
          value={<StatusBadge status={s.status} testId="status-badge" />}
          tone={statusTone(s.status)}
          mono={false}
        />
      </div>

      {/* Flags */}
      <div className="mb-6">
        <h3 className="text-xs uppercase tracking-widest text-gray-500 mb-3">Flags</h3>
        {(s.flags || []).length === 0 ? (
          <div className="text-sm text-gray-500">No flags raised.</div>
        ) : (
          s.flags.map((f, i) => <FlagCard key={`${f.label}-${i}`} flag={f} testId={`flag-${i}`} />)
        )}
      </div>

      {/* Duty components + DDP */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 bg-white rounded-md overflow-hidden" style={{ border: "1px solid var(--color-border)" }} data-testid="duty-components">
          <div className="px-4 py-3 border-b font-semibold text-[#0F2044] text-sm uppercase tracking-wider" style={{ borderColor: "var(--color-border)" }}>
            Duty Components (Entry Summary)
          </div>
          <table className="dr-table w-full">
            <thead>
              <tr>
                <th>Heading</th>
                <th>Description</th>
                <th className="text-right">Rate</th>
                <th className="text-right">Entered Value</th>
                <th className="text-right">Amount</th>
                <th>Type</th>
              </tr>
            </thead>
            <tbody>
              {(s.components || []).map((c, i) => (
                <tr key={`${c.tariff_heading || "row"}-${i}`}>
                  <td className="font-mono text-xs">{c.tariff_heading}</td>
                  <td>{c.description}</td>
                  <td className="num">{pct(c.rate_pct, 4)}</td>
                  <td className="num">{usd(c.entered_value)}</td>
                  <td className="num font-semibold">{usd(c.amount)}</td>
                  <td>
                    <span className={`text-xs uppercase font-semibold ${c.kind === "fee" ? "text-blue-600" : "text-[#0F2044]"}`}>
                      {c.kind}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: "#F8F9FC", fontWeight: 600 }}>
                <td colSpan="4" className="px-4 py-2 text-right text-xs uppercase tracking-wider text-gray-500">Customs Duty subtotal</td>
                <td className="num px-4 py-2">{usd(s.total_actual_customs_duty)}</td>
                <td></td>
              </tr>
              <tr style={{ background: "#F8F9FC" }}>
                <td colSpan="4" className="px-4 py-2 text-right text-xs uppercase tracking-wider text-gray-500">Fees subtotal (MPF + HMF)</td>
                <td className="num px-4 py-2">{usd(s.total_fees)}</td>
                <td></td>
              </tr>
              <tr style={{ background: "#0F2044", color: "white" }}>
                <td colSpan="4" className="px-4 py-2 text-right text-xs uppercase tracking-wider">Total Actual Duty and Fee Paid</td>
                <td className="num px-4 py-2 font-bold">{usd(s.total_actual_duty)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* DDP Valuation */}
        <div className="bg-white rounded-md p-5" style={{ border: "1px solid var(--color-border)" }} data-testid="ddp-panel">
          <div className="text-xs uppercase tracking-wider font-semibold text-[#0F2044] mb-3">
            DDP Valuation
          </div>
          <div className="space-y-2 text-sm">
            <Row k="Invoice total" v={usd(s.total_invoice_value)} />
            <Row k="Less: NDC" v={`- ${usd(s.ndc)}`} />
            <Row k="Total duty rate" v={pct((s.total_duty_rate || 0) * 100, 4)} />
            <hr style={{ borderColor: "var(--color-border)" }} />
            <Row k="Expected dutiable value" v={usd(s.expected_dutiable_value)} bold />
            <Row k="CBP entered value" v={usd(s.cbp_entered_value)} bold />
            <hr style={{ borderColor: "var(--color-border)" }} />
            <Row k="Residual DDP gap" v={usd(s.ddp_gap)} bold />
            <div className="pt-2">
              {Math.abs(s.ddp_gap || 0) < 500 ? (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700">
                  ✓ Verified (within tolerance)
                </span>
              ) : (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700">
                  ⚠ Broker review required
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Invoice lines */}
      <div className="bg-white rounded-md overflow-hidden" style={{ border: "1px solid var(--color-border)" }} data-testid="invoice-lines">
        <div className="px-4 py-3 border-b font-semibold text-[#0F2044] text-sm uppercase tracking-wider" style={{ borderColor: "var(--color-border)" }}>
          Invoice Line Items
        </div>
        <div className="overflow-x-auto">
        <table className="dr-table w-full text-sm">
          <thead>
            <tr>
              <th>Part</th>
              <th>Description</th>
              <th>PO</th>
              <th className="text-right">Qty</th>
              <th className="text-right">Unit Price</th>
              <th>HTS</th>
              <th className="text-right">Duty / Unit</th>
              <th className="text-right">Est. Duty</th>
              <th>Match</th>
            </tr>
          </thead>
          <tbody>
            {(s.lines || []).map((l, i) => (
              <tr key={`${l.part_number || "ln"}-${l.po_number || ""}-${i}`}>
                <td className="font-mono text-xs">{l.part_number}</td>
                <td>{l.description}</td>
                <td className="font-mono text-xs">{l.po_number}</td>
                <td className="num">{Number(l.quantity || 0).toLocaleString()}</td>
                <td className="num">{usd(l.unit_price)}</td>
                <td className="font-mono text-xs">{l.hts_code}</td>
                <td className="num">{usd(l.duty_per_unit)}</td>
                <td className="num font-semibold">{usd(l.estimated_duty)}</td>
                <td>
                  <div className="flex items-center gap-1.5">
                    <MatchIcon status={l.match_status} />
                    <span className="text-xs font-semibold">{l.match_status}</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ background: "#F8F9FC", fontWeight: 600 }}>
              <td colSpan="7" className="px-4 py-2 text-right text-xs uppercase tracking-wider text-gray-500">Total estimated duty</td>
              <td className="num px-4 py-2">{usd(s.total_estimated_duty)}</td>
              <td></td>
            </tr>
            <tr style={{ background: "#F8F9FC", fontWeight: 600 }}>
              <td colSpan="7" className="px-4 py-2 text-right text-xs uppercase tracking-wider text-gray-500">Total invoice value</td>
              <td className="num px-4 py-2">{usd(s.total_invoice_value)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(15, 32, 68, 0.55)" }}
          data-testid="delete-modal"
          onClick={() => !deleting && setConfirmDelete(false)}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-[#0F2044] mb-2">Delete this shipment?</h3>
            <div className="bg-slate-50 rounded-md p-3 mb-4 text-sm" style={{ border: "1px solid var(--color-border)" }}>
              <div className="font-mono text-xs text-gray-500">{s.reference}</div>
              <div className="font-semibold text-[#0F2044]">{s.invoice_number} · {s.customer_name}</div>
              <div className="text-xs text-gray-500 mt-1">
                Estimated {usd(s.total_estimated_duty)} · Actual {usd(s.total_actual_duty)}
              </div>
            </div>
            <p className="text-xs text-gray-500 mb-5">
              This removes the shipment from all reports and dashboards. The original Excel/PDF files
              uploaded for this shipment will remain in object storage. This cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                data-testid="delete-cancel"
                className="px-4 py-2 rounded-md text-sm bg-white hover:bg-slate-50"
                style={{ border: "1px solid var(--color-border)" }}
              >Cancel</button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                data-testid="delete-confirm"
                className="px-4 py-2 rounded-md text-sm font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
              >{deleting ? "Deleting…" : "Yes, delete"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Row = ({ k, v, bold }) => (
  <div className="flex items-center justify-between">
    <span className="text-gray-500">{k}</span>
    <span className={`font-mono ${bold ? "font-bold text-[#0F2044]" : ""}`} style={{ fontVariantNumeric: "tabular-nums" }}>{v}</span>
  </div>
);

export default Result;
